/**
 * Shared NodeRow component - extracted from TaigaSheetDetailPage.tsx
 * for reuse in both TaigaSheetDetailPage and MentionNodeTree.
 */
import type { FC, ReactNode } from "react"
import { useState, useRef } from "react"

import * as HoverCard from "@radix-ui/react-hover-card"

import styles from "./NodeRow.module.scss"
import { useSessionSelection } from "./SessionSelectionContext"

import PenIcon from "@/lib/images/PenIcon"

import { openClaudeSession, parseSlackText } from "../slack_mentions/utils"

// ─── Types ──────────────────────────────────────────────────────

export type SharedNode = {
	id: string
	milestoneId: string | null
	parentNodeId: string | null
	name: string
	parallelPriority: number | null
	isArchived: boolean
	order: number
	isDone: boolean
	isCollapsed: boolean
	session: {
		threadTs: string
		channelId: string
		sessionStatus: string
		// Remote Control (Claude アプリからの操作) が現在有効かどうか
		isRemoteControlEnabled: boolean
		memo: string | null
		ccSessionId: string | null
		// claude.ai 側のセッション ID (session_01...)。会話ページ URL に使う。未解決は null
		claudeRemoteControlSessionId: string | null
		worktreePath?: string | null
	} | null
}

// ─── Helpers ────────────────────────────────────────────────────

// Build tree from flat node list
export const buildTree = (
	nodes: SharedNode[],
	parentId: string | null
): SharedNode[] => {
	return nodes
		.filter((n) => n.parentNodeId === parentId && !n.isArchived)
		.sort((a, b) => a.order - b.order)
}

export const hasChildren = (nodes: SharedNode[], nodeId: string): boolean => {
	return nodes.some((n) => n.parentNodeId === nodeId && !n.isArchived)
}

// dragover ハンドラが実際に使うプロパティだけの共通型。
// ネイティブの React.DragEvent と、埋め込み時の pointer フォールバックが作る
// 合成イベントの両方がこれを満たす。
export type NodeDragOverEvent = {
	preventDefault: () => void
	clientX: number
	currentTarget: Element
}

// HTML5 Drag & Drop が使えない環境向けに、pointer イベントで DnD を再現して
// 既存の onDragStart / onDragOver / onDrop / onDragEnd に変換する:
// - taiga-browser (Cursor webview) の iframe 内では dragstart 自体が発火しない
//   (vscode#139111 の既知問題) → row 全体のマウス操作から pointer DnD を開始する
// - モバイル (touch) はネイティブ DnD が使えない → 専用ドラッグハンドル
//   (touch-action: none) からのみ pointer DnD を開始する。row 全体で touch を
//   拾うとスクロールを阻害するため、ハンドルに限定する
// PC の通常ブラウザは従来どおりネイティブ DnD を使う。
const isEmbedded = () =>
	typeof window !== "undefined" && window.parent !== window

// parallelPriority colors
const PARALLEL_PRIORITY_COLORS: Record<number, string> = {
	0: "#991b1b",
	1: "#dc2626",
	2: "#ea580c",
	3: "#d97706",
	4: "#ca8a04",
	5: "#65a30d",
	6: "#16a34a",
	7: "#0d9488",
	8: "#0284c7",
	9: "#7c3aed",
	10: "#9333ea",
}

export const getParallelPriorityColor = (pp: number): string => {
	return PARALLEL_PRIORITY_COLORS[pp] ?? "#888"
}

// Lightweight tooltip using HoverCard
export const Tip: FC<{ label: string; children: ReactNode }> = ({
	label,
	children,
}) => (
	<HoverCard.Root openDelay={80} closeDelay={0}>
		<HoverCard.Trigger asChild>{children}</HoverCard.Trigger>
		<HoverCard.Portal>
			<HoverCard.Content className={styles.tip} sideOffset={4}>
				{label}
				<HoverCard.Arrow className={styles.tipArrow} />
			</HoverCard.Content>
		</HoverCard.Portal>
	</HoverCard.Root>
)

// memo 表示用のツールチップ。memo 全文を表示し、Slack 形式リンク
// `<https://...>` / `<https://...|表示名>` を別タブで開くハイパーリンクにする。
// リンクをクリックできるよう closeDelay を持たせ、content にホバーを移しても閉じない。
const MemoTip: FC<{ memo: string; children: ReactNode }> = ({
	memo,
	children,
}) => (
	<HoverCard.Root openDelay={80} closeDelay={200}>
		<HoverCard.Trigger asChild>{children}</HoverCard.Trigger>
		<HoverCard.Portal>
			<HoverCard.Content
				className={styles.memoTip}
				sideOffset={4}
				onClick={(e) => e.stopPropagation()}
			>
				{parseSlackText(memo).map((segment, index) =>
					segment.type === "link" ? (
						<a
							key={index}
							href={segment.url}
							target="_blank"
							rel="noopener noreferrer"
							className={styles.memoTipLink}
							onClick={(e) => e.stopPropagation()}
						>
							{segment.label}
						</a>
					) : (
						<span key={index}>{segment.value}</span>
					)
				)}
				<HoverCard.Arrow className={styles.tipArrow} />
			</HoverCard.Content>
		</HoverCard.Portal>
	</HoverCard.Root>
)

// ─── Component ──────────────────────────────────────────────────

const NodeRow: FC<{
	node: SharedNode
	allNodes: SharedNode[]
	depth: number
	focusedNodeId: string | null
	isMobile: boolean
	hideDone: boolean
	onFocus: (id: string) => void
	onRename: (id: string, name: string) => void
	onAddChild: (milestoneId: string | null, parentId: string) => void
	onDelete: (id: string) => void
	onPriorityUp: (id: string) => void
	onPriorityDown: (id: string) => void
	onCreateSession: (nodeId: string, nodeName: string) => void
	onCompleteSession: (nodeId: string) => void
	onPendSession: (nodeId: string) => void
	onOpenSlack: (nodeId: string, threadTs: string, channelId: string) => void
	onCopyBranch: (nodeId: string) => void
	onForkSession?: (nodeId: string, nodeName: string) => void
	onOpenMemo: (node: SharedNode) => void
	editingNodeId: string | null
	setEditingNodeId: (id: string | null) => void
	draggedNodeId: string | null
	dragOverInfo: { nodeId: string; mode: "sibling" | "child" } | null
	onDragStart: (nodeId: string) => void
	onDragOver: (e: NodeDragOverEvent, nodeId: string) => void
	onDragLeave: () => void
	onDrop: (targetNodeId: string) => void
	onDragEnd: () => void
	onToggleCollapse: (id: string, isCollapsed: boolean) => void
	// cc セッション選択時の階層名 (例: "1-2-p1:{node.name}") 用。
	// sessionNamePath = root からの sort 位置パス ("1" / "1-2")、
	// sessionNameSuffix = 優先度サフィックス ("p1")。未指定なら node.name のまま
	sessionNamePath?: string
	sessionNameSuffix?: string
}> = ({
	node,
	allNodes,
	depth,
	focusedNodeId,
	isMobile,
	hideDone,
	onFocus,
	onRename,
	onAddChild,
	onDelete,
	onPriorityUp,
	onPriorityDown,
	onCreateSession,
	onCompleteSession,
	onPendSession,
	onOpenSlack,
	onCopyBranch,
	onForkSession,
	onOpenMemo,
	editingNodeId,
	setEditingNodeId,
	draggedNodeId,
	dragOverInfo,
	onDragStart,
	onDragOver,
	onDragLeave,
	onDrop,
	onDragEnd,
	onToggleCollapse,
	sessionNamePath,
	sessionNameSuffix,
}) => {
	const [editName, setEditName] = useState(node.name)
	const selection = useSessionSelection()
	const isExpanded = !node.isCollapsed
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const mobileMenuBtnRef = useRef<HTMLButtonElement>(null)
	const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(
		null
	)
	const isFolder = hasChildren(allNodes, node.id)
	const isLeaf = !isFolder
	const isEditing = editingNodeId === node.id
	const isFocused = focusedNodeId === node.id
	const children = buildTree(allNodes, node.id).filter(
		(c) => !hideDone || !c.isDone
	)
	const canAddChild = !node.session
	const isDragging = draggedNodeId === node.id
	const isDragOverSibling =
		dragOverInfo?.nodeId === node.id && dragOverInfo?.mode === "sibling"
	const isDragOverChild =
		dragOverInfo?.nodeId === node.id && dragOverInfo?.mode === "child"

	// 埋め込み時の pointer フォールバック用。
	// window に登録した pointermove / pointerup リスナーは登録時点の closure を
	// 保持し続けるため、prop を直接呼ぶと onDragStart で draggedNodeId が set される
	// 前の古い handler を掴んでしまう。ref 経由で常に最新の callback を呼ぶ。
	const dragCallbacksRef = useRef({
		onDragStart,
		onDragOver,
		onDragLeave,
		onDrop,
		onDragEnd,
	})
	dragCallbacksRef.current = {
		onDragStart,
		onDragOver,
		onDragLeave,
		onDrop,
		onDragEnd,
	}

	const startPointerDrag = (e: React.PointerEvent) => {
		const startX = e.clientX
		const startY = e.clientY
		let dragging = false
		let overNodeId: string | null = null
		let lastClientY = e.clientY

		const findRowAt = (x: number, y: number): HTMLElement | null =>
			(document
				.elementFromPoint(x, y)
				?.closest("[data-node-id]") as HTMLElement | null) ?? null

		// タッチドラッグ中は touch-action: none でページスクロールが止まるため、
		// 画面端に近づいたら最寄りのスクロールコンテナを自動スクロールする。
		const scrollContainer = ((): Element | null => {
			for (
				let cur: Element | null = e.currentTarget as Element;
				cur;
				cur = cur.parentElement
			) {
				const style = window.getComputedStyle(cur)
				if (
					/(auto|scroll)/.test(style.overflowY) &&
					cur.scrollHeight > cur.clientHeight
				) {
					return cur
				}
			}
			return document.scrollingElement
		})()

		let rafId = 0
		const autoScrollStep = () => {
			if (dragging && scrollContainer) {
				const edge = 56
				const { top, bottom } =
					scrollContainer === document.scrollingElement
						? { top: 0, bottom: window.innerHeight }
						: scrollContainer.getBoundingClientRect()
				if (lastClientY < top + edge) {
					scrollContainer.scrollTop -= 10
				} else if (lastClientY > bottom - edge) {
					scrollContainer.scrollTop += 10
				}
			}
			rafId = window.requestAnimationFrame(autoScrollStep)
		}
		rafId = window.requestAnimationFrame(autoScrollStep)

		const cleanup = () => {
			window.removeEventListener("pointermove", handleMove)
			window.removeEventListener("pointerup", handleUp)
			window.removeEventListener("pointercancel", handleCancel)
			window.cancelAnimationFrame(rafId)
			document.body.style.userSelect = ""
		}

		const handleMove = (ev: PointerEvent) => {
			lastClientY = ev.clientY
			if (!dragging) {
				// クリック/ダブルクリックと区別するため、少し動くまではドラッグ扱いしない
				if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) < 5) {
					return
				}
				dragging = true
				document.body.style.userSelect = "none"
				dragCallbacksRef.current.onDragStart(node.id)
				// draggedNodeId の state 反映 (re-render) を待ってから over 判定する
				return
			}
			const row = findRowAt(ev.clientX, ev.clientY)
			const rowNodeId = row?.dataset.nodeId
			if (row && rowNodeId && rowNodeId !== node.id) {
				overNodeId = rowNodeId
				dragCallbacksRef.current.onDragOver(
					{
						preventDefault: () => undefined,
						clientX: ev.clientX,
						currentTarget: row,
					},
					rowNodeId
				)
			} else {
				overNodeId = null
				dragCallbacksRef.current.onDragLeave()
			}
		}

		const handleUp = (ev: PointerEvent) => {
			if (dragging) {
				const row = findRowAt(ev.clientX, ev.clientY)
				const targetNodeId = row?.dataset.nodeId ?? overNodeId
				if (targetNodeId && targetNodeId !== node.id) {
					dragCallbacksRef.current.onDrop(targetNodeId)
				}
				dragCallbacksRef.current.onDragEnd()
			}
			cleanup()
		}

		const handleCancel = () => {
			if (dragging) dragCallbacksRef.current.onDragEnd()
			cleanup()
		}

		window.addEventListener("pointermove", handleMove)
		window.addEventListener("pointerup", handleUp)
		window.addEventListener("pointercancel", handleCancel)
	}

	const handleEmbeddedPointerDown = (e: React.PointerEvent) => {
		if (!isEmbedded()) return
		// マウス左ボタンのみ。touch は行全体で拾うとスクロールを阻害するため
		// 対象外にする (モバイルはドラッグハンドルからのみ開始する)。
		if (e.button !== 0 || e.pointerType !== "mouse") return
		if (
			e.target instanceof Element &&
			e.target.closest("button, input, textarea, a, [contenteditable='true']")
		) {
			return
		}
		startPointerDrag(e)
	}

	// モバイル: HTML5 DnD はタッチで発火しないため、行頭のドラッグハンドルからのみ
	// pointer フォールバックでドラッグを開始する。ハンドルは touch-action: none で
	// スクロールを奪うが、行の他の部分は通常どおりスクロールできる。
	const handleDragHandlePointerDown = (e: React.PointerEvent) => {
		if (e.pointerType === "mouse" && e.button !== 0) return
		e.stopPropagation()
		startPointerDrag(e)
	}

	const handleDoubleClick = () => {
		setEditingNodeId(node.id)
		setEditName(node.name)
	}

	const handleRenameSubmit = () => {
		const trimmed = editName.trim()
		if (!trimmed) {
			setEditingNodeId(null)
			return
		}
		if (trimmed !== node.name) {
			onRename(node.id, trimmed)
		}
		setEditingNodeId(null)
	}

	// Mobile: 3-dot menu actions
	// session 作成 (+Session) と ↑P/↓P は mobileActionRow に直接出すため、
	// ここ (kebab) には含めない。session ありのとき ↑P/↓P のみ kebab に残す。
	const mobileActions = () => {
		const actions: { label: string; action: () => void; variant?: string }[] =
			[]
		if (!node.isDone) {
			actions.push({
				label: "Done",
				action: () => {
					setMobileMenuOpen(false)
					onCompleteSession(node.id)
				},
			})
		}
		if (canAddChild) {
			actions.push({
				label: "+Child",
				action: () => {
					setMobileMenuOpen(false)
					onAddChild(node.milestoneId, node.id)
				},
			})
		}
		if (isLeaf) {
			actions.push({
				label: "↑P",
				action: () => {
					setMobileMenuOpen(false)
					onPriorityUp(node.id)
				},
			})
			actions.push({
				label: "↓P",
				action: () => {
					setMobileMenuOpen(false)
					onPriorityDown(node.id)
				},
			})
		}
		if (node.session) {
			actions.push({
				label: node.session.memo ? "メモ (有)" : "メモ",
				action: () => {
					setMobileMenuOpen(false)
					onOpenMemo(node)
				},
			})
			actions.push({
				label: "Branch",
				action: () => {
					setMobileMenuOpen(false)
					onCopyBranch(node.id)
				},
			})
		}
		actions.push({
			label: "Edit",
			action: () => {
				setMobileMenuOpen(false)
				setEditingNodeId(node.id)
				setEditName(node.name)
			},
		})
		actions.push({
			label: "Del",
			action: () => {
				setMobileMenuOpen(false)
				onDelete(node.id)
			},
			variant: "danger",
		})
		if (
			node.session &&
			!node.isDone &&
			node.session.sessionStatus !== "PENDING"
		) {
			actions.push({
				label: "Pend",
				action: () => {
					setMobileMenuOpen(false)
					onPendSession(node.id)
				},
			})
		}
		return actions
	}

	return (
		<>
			<div
				className={`${styles.nodeRow} ${node.session?.sessionStatus === "IDLE" ? styles.idleRow : ""} ${node.session?.sessionStatus === "BUSY" ? styles.busyRow : ""} ${node.session?.sessionStatus === "PENDING" ? styles.pendingRow : ""} ${isFocused ? styles.focused : ""} ${node.isDone ? styles.done : ""} ${isDragging ? styles.nodeDragging : ""} ${isDragOverSibling ? styles.nodeDragOverSibling : ""} ${isDragOverChild ? styles.nodeDragOverChild : ""}`}
				style={{ paddingLeft: 16 + depth * (isMobile ? 12 : 24) }}
				onClick={() => {
					onFocus(node.id)
				}}
				onDoubleClick={handleDoubleClick}
				data-node-id={node.id}
				onPointerDown={handleEmbeddedPointerDown}
				// 埋め込み時はネイティブ DnD を無効化して pointer フォールバックに一本化する。
				// 両方生かすと、ネイティブ dragstart が発火する環境 (webview 側の DnD 対応が
				// 進んだ場合や一般の iframe 埋め込み) で pointercancel がフォールバックの
				// handleCancel を呼び、ドラッグ state がリセットされて DnD が壊れるため。
				draggable={!isEmbedded()}
				onDragStart={(e) => {
					e.stopPropagation()
					onDragStart(node.id)
				}}
				onDragOver={(e) => {
					e.stopPropagation()
					onDragOver(e, node.id)
				}}
				onDragLeave={onDragLeave}
				onDrop={(e) => {
					e.stopPropagation()
					e.preventDefault()
					onDrop(node.id)
				}}
				onDragEnd={onDragEnd}
			>
				{selection && node.session && (
					// span でクリック当たり判定を広げつつ stopPropagation する。
					// checkbox 本体は小さく、少し外すと親の onClick (row focus) が
					// 発火してしまう。disabled 時も span がクリックを吸収する。
					<span
						className={styles.selectCheckboxWrap}
						onClick={(e) => e.stopPropagation()}
						onDoubleClick={(e) => e.stopPropagation()}
					>
						<input
							type="checkbox"
							className={styles.selectCheckbox}
							disabled={node.session.ccSessionId === null}
							title={
								node.session.ccSessionId === null
									? "cc-server の session id が未登録のため選択できません"
									: undefined
							}
							checked={
								node.session.ccSessionId !== null &&
								selection.selectedCcSessions.has(node.session.ccSessionId)
							}
							onChange={() => {
								if (node.session?.ccSessionId) {
									const prefix =
										sessionNameSuffix !== undefined
											? `${sessionNamePath ? `${sessionNamePath}-` : ""}${sessionNameSuffix}:`
											: ""
									selection.toggleCcSession({
										ccSessionId: node.session.ccSessionId,
										name: `${prefix}${node.name}`,
										worktreePath: node.session.worktreePath ?? null,
									})
								}
							}}
						/>
					</span>
				)}
				{isMobile && (
					<span
						className={styles.dragHandle}
						onPointerDown={handleDragHandlePointerDown}
						onClick={(e) => e.stopPropagation()}
						onDoubleClick={(e) => e.stopPropagation()}
					>
						⠿
					</span>
				)}
				{isFolder && (
					<button
						className={styles.expandBtn}
						onClick={(e) => {
							e.stopPropagation()
							onToggleCollapse(node.id, !node.isCollapsed)
						}}
					>
						{isExpanded ? "▾" : "▸"}
					</button>
				)}
				{isLeaf && <span className={styles.leafIcon}>•</span>}

				{isEditing ? (
					<textarea
						className={styles.renameInput}
						value={editName}
						onChange={(e) => setEditName(e.target.value)}
						onKeyDown={(e) => {
							e.stopPropagation()
							if (
								e.key === "Enter" &&
								(isMobile || e.metaKey || e.ctrlKey) &&
								!e.nativeEvent.isComposing
							) {
								e.preventDefault()
								handleRenameSubmit()
							}
							if (e.key === "Escape") {
								e.preventDefault()
								setEditingNodeId(null)
							}
						}}
						onBlur={handleRenameSubmit}
						rows={1}
						autoFocus
					/>
				) : (
					<span
						className={`${styles.nodeName} ${node.isDone ? styles.strikethrough : ""}`}
					>
						{node.name}
					</span>
				)}

				{!isMobile && isLeaf && node.parallelPriority != null && (
					<span
						className={styles.priorityBadge}
						style={{
							background: getParallelPriorityColor(node.parallelPriority),
							color: "white",
						}}
					>
						{node.parallelPriority}
					</span>
				)}

				{!isMobile && node.session && (
					<span
						className={`${styles.statusBadge} ${node.session.sessionStatus === "BUSY" ? styles.busy : node.session.sessionStatus === "PENDING" ? styles.pending : styles.idle}`}
					>
						{node.session.sessionStatus.toLowerCase()}
					</span>
				)}

				{!isMobile &&
					node.session &&
					(node.session.memo ? (
						<MemoTip memo={node.session.memo}>
							<button
								className={styles.memoIndicator}
								onClick={(e) => {
									e.stopPropagation()
									onOpenMemo(node)
								}}
							>
								<PenIcon color="rgb(213, 196, 56)" />
							</button>
						</MemoTip>
					) : (
						<Tip label="メモを編集 [M]">
							<button
								className={styles.memoIndicator}
								onClick={(e) => {
									e.stopPropagation()
									onOpenMemo(node)
								}}
							>
								<PenIcon color="rgb(5, 5, 5)" />
							</button>
						</Tip>
					))}

				{!isMobile && node.session?.threadTs && (
					<Tip label="Slack を開く [1]">
						<button
							className={styles.slackLink}
							onClick={(e) => {
								e.stopPropagation()
								onOpenSlack(
									node.id,
									node.session!.threadTs,
									node.session!.channelId
								)
							}}
						>
							Slack
						</button>
					</Tip>
				)}

				{/* Claude ボタンは RC (Remote Control) 有効セッションだけに出す。
				    RC バッジは廃止し、このボタンの有無が RC の目印を兼ねる。 */}
				{!isMobile &&
					node.session?.isRemoteControlEnabled &&
					node.session.ccSessionId && (
						<Tip label="Claude アプリでこのセッションを開く (Remote Control 有効セッション)">
							<button
								className={styles.claudeBtn}
								onClick={(e) => {
									e.stopPropagation()
									openClaudeSession(
										node.session!.claudeRemoteControlSessionId,
										node.session!.ccSessionId!
									)
								}}
							>
								Claude
							</button>
						</Tip>
					)}

				{!isMobile && node.session && (
					<Tip label="Branch 名をコピー [2]">
						<button
							className={styles.branchBtn}
							onClick={(e) => {
								e.stopPropagation()
								onCopyBranch(node.id)
							}}
						>
							Branch
						</button>
					</Tip>
				)}

				{!isMobile && node.session && onForkSession && (
					<Tip label="Session をフォーク（同じ階層に worktree・会話履歴を引き継いだ複製セッションを作成）">
						<button
							className={styles.branchBtn}
							onClick={(e) => {
								e.stopPropagation()
								onForkSession(node.id, node.name)
							}}
						>
							Fork
						</button>
					</Tip>
				)}

				{/* Desktop: inline action buttons */}
				{!isMobile && (
					<div className={styles.nodeActions}>
						{isLeaf && !node.session && (
							<Tip label="Session を作成 [S]">
								<button
									className={styles.actionBtn}
									onClick={(e) => {
										e.stopPropagation()
										onCreateSession(node.id, node.name)
									}}
								>
									+Session
								</button>
							</Tip>
						)}
						{!node.isDone && (
							<Tip label="完了にする [Q]">
								<button
									className={`${styles.actionBtn} ${styles.completeBtn}`}
									onClick={(e) => {
										e.stopPropagation()
										onCompleteSession(node.id)
									}}
								>
									Done
								</button>
							</Tip>
						)}
						{canAddChild && (
							<Tip label="子ノードを追加 [A]">
								<button
									className={styles.actionBtn}
									onClick={(e) => {
										e.stopPropagation()
										onAddChild(node.milestoneId, node.id)
									}}
								>
									+Child
								</button>
							</Tip>
						)}
						<Tip label="Priority Up [3]">
							<button
								className={styles.actionBtn}
								onClick={(e) => {
									e.stopPropagation()
									onPriorityUp(node.id)
								}}
							>
								↑P
							</button>
						</Tip>
						<Tip label="Priority Down [4]">
							<button
								className={styles.actionBtn}
								onClick={(e) => {
									e.stopPropagation()
									onPriorityDown(node.id)
								}}
							>
								↓P
							</button>
						</Tip>
						<Tip label="名前を変更 [E]">
							<button
								className={styles.actionBtn}
								onClick={(e) => {
									e.stopPropagation()
									setEditingNodeId(node.id)
									setEditName(node.name)
								}}
							>
								Edit
							</button>
						</Tip>
						<Tip label="アーカイブ [6]">
							<button
								className={`${styles.actionBtn} ${styles.deleteBtn}`}
								onClick={(e) => {
									e.stopPropagation()
									onDelete(node.id)
								}}
							>
								Del
							</button>
						</Tip>
						{node.session &&
							!node.isDone &&
							node.session.sessionStatus !== "PENDING" && (
								<Tip label="Pending にする [7]">
									<button
										className={styles.actionBtn}
										onClick={(e) => {
											e.stopPropagation()
											onPendSession(node.id)
										}}
									>
										Pend
									</button>
								</Tip>
							)}
					</div>
				)}

				{/* Mobile: 3-row action group.
				    Row1: priority + status(idle/busy) + 3-dot menu
				    Row2: Slack + Fork (session なし leaf のときは +session)
				    Row3: Claude (cc session があるとき)
				    → 右側を細く固定して左のノード名に横幅を最大限あける */}
				{isMobile && (
					<div className={styles.mobileActionGroup}>
						<div className={styles.mobileActionRow}>
							{isLeaf && node.parallelPriority != null && (
								<span
									className={styles.priorityBadge}
									style={{
										background: getParallelPriorityColor(node.parallelPriority),
										color: "white",
									}}
								>
									{node.parallelPriority}
								</span>
							)}
							{node.session && (
								<span
									className={`${styles.statusBadge} ${node.session.sessionStatus === "BUSY" ? styles.busy : node.session.sessionStatus === "PENDING" ? styles.pending : styles.idle}`}
								>
									{node.session.sessionStatus.toLowerCase()}
								</span>
							)}
							<div className={styles.mobileMenuWrapper}>
								<button
									ref={mobileMenuBtnRef}
									className={styles.mobileMenuBtn}
									onClick={(e) => {
										e.stopPropagation()
										if (!mobileMenuOpen && mobileMenuBtnRef.current) {
											const rect =
												mobileMenuBtnRef.current.getBoundingClientRect()
											setMenuPos({
												top: rect.bottom + 4,
												right: window.innerWidth - rect.right,
											})
										}
										setMobileMenuOpen(!mobileMenuOpen)
									}}
								>
									⋯
								</button>
								{mobileMenuOpen && menuPos && (
									<>
										<div
											className={styles.mobileMenuOverlay}
											onClick={() => setMobileMenuOpen(false)}
										/>
										<div
											className={styles.mobileMenuFixed}
											style={{ top: menuPos.top, right: menuPos.right }}
										>
											{mobileActions().map((a) => (
												<button
													key={a.label}
													className={`${styles.mobileMenuItem} ${a.variant === "danger" ? styles.mobileMenuDanger : ""}`}
													onClick={(e) => {
														e.stopPropagation()
														a.action()
													}}
												>
													{a.label}
												</button>
											))}
										</div>
									</>
								)}
							</div>
						</div>
						{node.session ? (
							<div className={styles.mobileActionRow}>
								{node.session.threadTs && (
									<button
										className={styles.slackLink}
										onClick={(e) => {
											e.stopPropagation()
											onOpenSlack(
												node.id,
												node.session!.threadTs,
												node.session!.channelId
											)
										}}
									>
										Slack
									</button>
								)}
								{onForkSession && (
									<button
										className={styles.branchBtn}
										onClick={(e) => {
											e.stopPropagation()
											onForkSession(node.id, node.name)
										}}
									>
										Fork
									</button>
								)}
							</div>
						) : (
							isLeaf && (
								<div className={styles.mobileActionRow}>
									<button
										className={styles.mobileSessionBtn}
										onClick={(e) => {
											e.stopPropagation()
											onCreateSession(node.id, node.name)
										}}
									>
										session
									</button>
								</div>
							)
						)}
						{/* Row3: Claude アプリでセッションを開く (Slack ボタンの下)。
						    RC 有効セッションだけに出し、RC バッジの代わりを兼ねる。 */}
						{node.session?.isRemoteControlEnabled &&
							node.session.ccSessionId && (
								<div className={styles.mobileActionRow}>
									<button
										className={styles.claudeBtn}
										onClick={(e) => {
											e.stopPropagation()
											openClaudeSession(
												node.session!.claudeRemoteControlSessionId,
												node.session!.ccSessionId!
											)
										}}
									>
										Claude
									</button>
								</div>
							)}
					</div>
				)}
			</div>

			{isExpanded &&
				children.map((child, i) => (
					<NodeRow
						key={child.id}
						node={child}
						allNodes={allNodes}
						depth={depth + 1}
						focusedNodeId={focusedNodeId}
						isMobile={isMobile}
						hideDone={hideDone}
						onFocus={onFocus}
						onRename={onRename}
						onAddChild={onAddChild}
						onDelete={onDelete}
						onPriorityUp={onPriorityUp}
						onPriorityDown={onPriorityDown}
						onCreateSession={onCreateSession}
						onCompleteSession={onCompleteSession}
						onPendSession={onPendSession}
						onOpenSlack={onOpenSlack}
						onCopyBranch={onCopyBranch}
						onForkSession={onForkSession}
						onOpenMemo={onOpenMemo}
						editingNodeId={editingNodeId}
						setEditingNodeId={setEditingNodeId}
						draggedNodeId={draggedNodeId}
						dragOverInfo={dragOverInfo}
						onDragStart={onDragStart}
						onDragOver={onDragOver}
						onDragLeave={onDragLeave}
						onDrop={onDrop}
						onDragEnd={onDragEnd}
						onToggleCollapse={onToggleCollapse}
						sessionNamePath={
							sessionNamePath !== undefined
								? `${sessionNamePath}-${i + 1}`
								: undefined
						}
						sessionNameSuffix={sessionNameSuffix}
					/>
				))}
		</>
	)
}

export default NodeRow
